import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { TimelineSection } from '../../../../_models/timeline-models/section-listing.model';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TimelineDirective } from '../../../../_directives/timeline.directive';
import { BaseLearningSectionComponent } from '../../../../shared/learning-section/base-learning-section/base-learning-section.component';
import { LearningSectionEnum } from '../../../../student-bookings/enum/learning-section.enum';
import { FeatureHelperService } from '../../../../_helpers/feature-helper/feature-helper.service';
import { TimelineSectionStatus } from '../../../../_models/timeline-models/timeline-section-status.enum';
import { LearningSectionFlowControlHelperService } from '../../../../_helpers/learning-section-flow-control-helper/learning-section-flow-control-helper.service';
import { LearningSection, LearningSectionList } from '../../../../_models/student-bookings/learning-section-list.model';
import { TimelineSectionStudentInfo } from '../../../../_models/timeline-models/timeline-section-student-info.model';
import { formatDate } from "@angular/common";
import { parse } from 'date-fns';
import { DynamicConfirmModalComponent } from '../../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';

interface DialogData {
  editMode: boolean;
  timelineSection: TimelineSection;
  add: boolean;
  students: TimelineSectionStudentInfo[];
  placementId: number;
  subject: string;
  disableDate: boolean;
}

@Component({
    selector: 'app-edit-learning-section-modal',
    templateUrl: './edit-learning-section-modal.component.html',
    styleUrls: ['./edit-learning-section-modal.component.css', '../../tutor-report-timeline-learning-section-component.css'],
    standalone: false
})
export class EditLearningSectionModalComponent implements OnInit {
  @ViewChild(TimelineDirective, { static: true }) learningSectionDirective!: TimelineDirective;

  public innerComponent: BaseLearningSectionComponent;
  public learningSections: LearningSection[] = LearningSectionList.learnerSections;
  public sectionStatus = TimelineSectionStatus;
  public sectionTitle: string = '';
  public operationDescription: string = 'Edit';
  public groupStudentsNameList: string = "";
  public minSectionDate: Date = new Date();
  public changesMade: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<EditLearningSectionModalComponent>,
    public featureHelperService: FeatureHelperService,
    public flowHelperService: LearningSectionFlowControlHelperService,
    private dialog: MatDialog
  ) { 
    this.minSectionDate.setFullYear(this.minSectionDate.getFullYear() - 1);
    this.minSectionDate.setHours(12, 0, 0, 0);
  }

  ngOnInit() {
    this.renderSection();
    if (this.data.add) {
      this.operationDescription = 'Add a New';
    }
    if (this.data.students) {
      this.data.students.forEach((student: TimelineSectionStudentInfo, index: number) => {
        if (index == 0) {
          this.groupStudentsNameList = student.studentFullName;
        } else {
          this.groupStudentsNameList += ", " + student.studentFullName;
        }
      });

      if (this.data.students.length > 1) {
        let tempSections: LearningSection[] = [];
        this.learningSections.forEach(section => {
          if (section.sectionType !== LearningSectionEnum.Assessment) {
            tempSections.push(section);
          }
        });
        this.learningSections = tempSections;
      }
    }
  }

  sectionClicked(learningSection: LearningSectionEnum): void {
    this.data.timelineSection = new TimelineSection();
    this.data.timelineSection.sectionType = learningSection.toLowerCase();
    this.data.timelineSection.status = TimelineSectionStatus.New;
    this.data.timelineSection.subject = this.data.subject;
    this.data.timelineSection.sectionDate = new Date(Date.now()).toISOString();
    this.renderSection();
  }

  renderSection(): void {
    if (this.data.timelineSection) {

      this.innerComponent = this.learningSectionDirective.renderSection(this.data.timelineSection, this.data.editMode, this.data.placementId, this.data.students, this.data.subject, this.minSectionDate);
      this.sectionTitle = this.innerComponent.sectionTitle;
      this.innerComponent.students = this.data.students;
      this.innerComponent._sectionStatusUpdated.subscribe((status: TimelineSectionStatus) => {
        this.data.timelineSection.status = status;
        this.changesMade = true;
      });
    }
  }

  showDateMandatoryLabel(): boolean {
    if (typeof (this.data.timelineSection.sectionDate) === 'string') {
      return !(this.data.timelineSection.sectionDate && this.data.timelineSection.sectionDate.length > 0);
    } else {
      return this.data.timelineSection.sectionDate === null;
    }
  }

  getClass(section: LearningSectionEnum): string {
    return 'tl-ls__tile--' + section.toString().toLowerCase();
  }

  public isFormEditable(): boolean {
    return !this.data.disableDate && this.flowHelperService.CanEditTimelineSection(this.data.timelineSection);
  }

  public save(): void {
    if (this.innerComponent.hasMethodInstantiated("save")) {
      this.changesMade = true;
      this.innerComponent.save(this.dialogRef);
    }
  }

  public delete(): void {
    const confirmRef = this.dialog.open(DynamicConfirmModalComponent, {
      data: { confirmMessage: "This action will permanently remove this record, are you sure you wish to continue?" },
      panelClass: 'my-confirm-dialog'
    });
    confirmRef.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        if (this.innerComponent.hasMethodInstantiated("delete") && this.deleteButtonEnabled()) {
          this.changesMade = true;
          this.innerComponent.delete(this.dialogRef);
          this.dialogRef.close(true);
        }
      } 
    }); 
  }

  public deleteButtonEnabled(): boolean {
    if (!this.data.timelineSection) {
      return false;
    }
    return this.featureHelperService.hasFeature('IndividualLearningReportsApprove');
  }

  

  public submit(): void {
    if (this.innerComponent.hasMethodInstantiated("submit")) {
      this.changesMade = true;
      this.innerComponent.submit(this.dialogRef);
    }
  }

  public approve(): void {
    if (this.innerComponent.hasMethodInstantiated("approve")) {
      this.changesMade = true;
      this.innerComponent.approve(this.dialogRef);
    }
  }

  public reject(): void {
    if (this.innerComponent.hasMethodInstantiated("reject")) {
      this.changesMade = true;
      this.innerComponent.reject(this.dialogRef);
    } 
  }

  public unlock(): void {
    if (this.innerComponent.hasMethodInstantiated("unlock")) {
      this.changesMade = true;
      this.innerComponent.unlock(this.dialogRef);
    } 
  }

  public saveButtonEnabled(): boolean {
    if (!this.data.timelineSection) {
      return false;
    }
    return this.data.timelineSection.status === TimelineSectionStatus.New ||
      this.data.timelineSection.status === TimelineSectionStatus.Saved ||
      this.data.timelineSection.status === TimelineSectionStatus.Rejected &&
      (this.featureHelperService.hasFeature('IndividualLearningReportsSave')
        && !this.featureHelperService.hasFeature('TutorReportsSave'));
  }

  public submitButtonEnabled(): boolean {
    if (!this.data.timelineSection) {
      return false;
    }
    return (this.data.timelineSection.status === TimelineSectionStatus.New ||
      this.data.timelineSection.status === TimelineSectionStatus.Saved ||
      this.data.timelineSection.status === TimelineSectionStatus.Rejected) &&
      this.featureHelperService.hasFeature('TutorReportsSave');
  }

  public approveButtonEnabled(): boolean {
    if (!this.data.timelineSection) {
      return false;
    }
    return this.data.timelineSection.status === TimelineSectionStatus.Submitted && this.featureHelperService.hasFeature('IndividualLearningReportsApprove');
  }

  public rejectButtonEnabled(): boolean {
    if (!this.data.timelineSection) {
      return false;
    }
    return this.data.timelineSection.status === TimelineSectionStatus.Rejected && this.featureHelperService.hasFeature('IndividualLearningReportsReject');
  }

  public unlockButtonEnabled(): boolean {
    if (!this.data.timelineSection) {
      return false;
    }
    return this.data.timelineSection.status === TimelineSectionStatus.Approved && this.featureHelperService.hasFeature('UnlockIndividualLearningReports');
  }

  public showFormControls(): boolean {
    return this.data.timelineSection && this.data.timelineSection.sectionType !== 'assessment'
  }

}
